/**
 * Contractors.tsx — F11 T11.2b + Krok 1 kreatora widoków list (port z SellCall).
 *
 * Tabela kontrahentow z konfigurowalnymi kolumnami (ConfigurableTable) — kolumny rdzenne
 * DATA-DRIVEN z katalogu pól (`GET /system-modules/contractors/fields`), wzorzec pilotowy
 * `PurchaseInvoices.tsx`: `buildCoreColumns` wylicza klucz odpowiedzi ze sluga mechanicznie
 * (slug→camelCase), więc ZERO mapy id-kolumny→slug per strona (dawny `CORE_COLUMN_ID_TO_SLUG`
 * usunięty). Nazwa/typ podmiotu/status mają bogaty render przez rejestr nadpisań
 * (`CONTRACTOR_OVERRIDES`): nazwa+podpis „nazwa skrócona" z linkiem do detalu, typ podmiotu
 * (styl wygaszony, bez tłumacza w komórce — 1:1 sprzed migracji), status jako plakietka
 * z etykietą i18n. NIP/telefon/email idą generykiem (typ→komórka, katalog). Firma
 * (`home_company_id`) idzie dedykowanym `buildCompanyColumn` (record-select filtr) — WYKLUCZONA
 * z `buildCoreColumns` (`excludeSlugs`), tak jak `owner_user_id`/`assigned_user_id` (kontrahenci
 * nie mają dziś kolumny „Opiekun"/„Przypisany" na liście — pre-migration strona też nie miała).
 * Konfiguracja kolumn kontrahentów (nadpisania, wykluczenia, argumenty kolumny firmy, slugi
 * `?columns=`, mapa id→slug, typ wiersza) żyje w `lib/contractors/contractor-columns` — wspólna ze
 * stroną i z sekcją kokpitu decyzji.
 * Pola dodatkowe (ext) przez `buildExtColumns`. Kolejność kolumn na liście idzie teraz za
 * `sortOrder` katalogu (nie za zaszytą kolejnością strony sprzed migracji); widoczność
 * NIP/telefonu/emaila jedzie na `showInList` katalogu, nie na twardym include tej strony.
 *
 * Layout listy zapisywany w `system_module_layouts.list_config` przez backend
 * `/api/system-modules/contractors/layouts/...`. Domyślny widok systemowy
 * seedowany przy provisioningu tenanta (wszystkie kolumny visible, sort po nazwie).
 *
 * Backend: GET /api/contractors — już na wspólnym silniku list (`runListEngine`, wariant B,
 * patrz `apps/api/lib/contractors/contractor-list-descriptor.ts`); visibility filter Model B
 * (bogatszy niż `company_id IN (...)` — „firma macierzysta w zakresie LUB udostępniono").
 * DELETE /:id (soft-delete). Detail page: /contractors/new + /contractors/:id (osobny tor T11.2b).
 *
 * UX: pasek chipów grup ze słownika `contractor_groups` nad listą (zastąpił stare
 * zakładki kategorii kontrahentów, F12.N Etap 2bis.1 → decyzja 2026-08-18), globalny
 * search + filtr statusu, bulk toolbar (export CSV + archiwizacja).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Building2, Pencil, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import {
  type ColumnDefinition,
  type ColumnFilters,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  ListActionsMenu,
  ListBuilderMenu,
  ListFilterBar,
  ListTopWidgets,
  ListViewModeToggle,
  QuickFilterBar,
  RowActionsMenu,
  useListViewMode,
  useTableLayout,
} from '@/components/configurable-table';
import {
  type ListResponseMeta,
  useServerListControls,
} from '@/components/configurable-table/useServerListControls';
import { ContractorImportModal } from '@/components/contractors/ContractorImportModal';
import { GroupChipBar } from '@/components/groups/GroupChipBar';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useForcedListLayoutId } from '@/hooks/useForcedListLayoutId';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import {
  buildContractorOverrides,
  CONTRACTOR_ALWAYS_SLUGS,
  CONTRACTOR_COLUMN_COMPANIONS,
  CONTRACTOR_COLUMN_ID_TO_SLUG,
  CONTRACTOR_COMPANY_COLUMN_ARGS,
  type ContractorRow,
  CONTRACTORS_EXCLUDE_SLUGS,
} from '@/lib/contractors/contractor-columns';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  computeVisibleColumnParams,
  type ExtRefLabels,
  FieldCatalogErrorState,
  useExtRefLabelsGetter,
  useSystemModuleFields,
} from '@/lib/ext-list-columns';
import { useListActions } from '@/lib/list-actions/useListActions';
import { useEngineListExport } from '@/lib/list-export/useEngineListExport';
import { isExtraFilterField } from '@/lib/list-filter-extra-fields';
import { resolveFilterVariable } from '@/lib/list-filter-variables';
import { runConflictText } from '@/lib/record-procedures/run-texts';
import { extractRunConflict } from '@/lib/record-procedures/runs-api';
import { companyIdParam, useActiveCompanyScope } from '@/lib/use-active-company';

const CONTRACTORS_KEY = 'contractors';

/**
 * Lista nie zna układu formularza kontrahenta, więc nie zna napisów jego przycisków SQL — komunikat o
 * blokadzie nazywa zadanie nazwą procedury z katalogu (z odpowiedzi serwera).
 */
const NO_BUTTON_LABELS: ReadonlyMap<string, string> = new Map();

interface ListResponse {
  contractors: ContractorRow[];
  limit: number;
  offset: number;
  total: number;
  /** Slugi kolumn CORE sortowalnych serwerowo (silnik list) — patrz `useServerListControls`. */
  meta?: ListResponseMeta;
  /** Etykiety pól `reference` (sibling do wierszy) — obecne gdy `?columns=` obejmuje
   *  przynajmniej jedno takie pole. Patrz `ExtRefLabels`. */
  extRefLabels?: ExtRefLabels;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function Contractors() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();

  // Tryb "Wszystkie firmy" — import (tworzy nowe rekordy) i masowa archiwizacja
  // (backend wymaga X-Company-Id, patrz requireContractorVisibility/requireCompany)
  // nie mogą działać bez konkretnej firmy. Interceptor w tym trybie nie wysyła
  // headera, więc obie akcje są tu zablokowane z tooltipem zamiast dawać 400.
  const activeCompanyScope = useActiveCompanyScope();

  // Model B (widoczność per-firma) — tryb "Wszystkie firmy" omija ?companyId=, tryb
  // konkretnej firmy dokleja go do fetchu i klucza zapytania (§4.8 CLAUDE.md). `companies`
  // zasila statyczne opcje filtra multi-choice kolumny „Firma" (buildCompanyColumn, bez
  // fetchu /distinct).
  const companyId = companyIdParam(activeCompanyScope);
  const companies = useMemo(() => me?.companies ?? [], [me?.companies]);

  // Przełącznik „kosza" (gcrecord) zdjęty z kartotek (PHASE 25, decyzja właścicielki): „Usuń"
  // = miękkie usunięcie bez widoku odzyskiwania w UI (dojdzie z centrum uprawnień). Archiwum
  // (flaga) jest wyłącznie na dokumentach.
  const [search, setSearch] = useState('');
  /**
   * Filtr nad listą = GRUPY ze słownika `contractor_groups` (decyzja 2026-08-18). Wiele grup
   * naraz, semantyka OR. Zastąpił zakładki starego drzewka kategorii kontrahentów
   * (`contractor_categories`), usunięte razem z całym drzewkiem.
   */
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const debouncedSearch = useDebouncedValue(search, 300);

  // Sort serwerowy + paginacja (silnik list) — wspólny hook, patrz useServerListControls.ts.
  // `sortableSlugs` — `meta.sortable` z ostatniej odpowiedzi listy (`listQ` niżej), zasilane
  // PO zapytaniu (analogicznie do `getRefLabels`/`setRefLabels`), ale jako REALNY stan (nie
  // ref) — kolumny core muszą się przeliczyć, gdy metadane dotrą (patrz `columns` niżej).
  // Bez `columnIdToSortSlug` — id kolumny = slug na tej liście (mechaniczny `buildCoreColumns`),
  // więc fallback hooka do `columnId` już jest właściwym slugiem sortu.
  const controls = useServerListControls();
  const [sortableSlugs, setSortableSlugs] = useState<string[] | undefined>(undefined);
  const [filterableSlugs, setFilterableSlugs] = useState<string[] | undefined>(undefined);

  // Zmiana zakresu firmy (CompanySwitcher) zawęża wynik — reset strony jak przy filtrach/sorcie
  // (inaczej user może utknąć na pustej stronie N).
  useEffect(() => {
    controls.resetPage();
  }, [companyId, controls.resetPage]);

  // Katalog pól modułu `contractors` (core + ext) — źródło kolumn dodatkowych
  // generowanych dynamicznie poniżej (`extColumns`) i etykiet do `?columns=`.
  const fieldsQ = useSystemModuleFields('contractors');

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Kolumny FAKTYCZNIE widoczne w tabeli (kolejność z ekranu) — zasila eksport CSV (E16).
  // `null` przed pierwszym zgłoszeniem tabeli → eksport bierze kolumny domyślnie widoczne.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Filtry per kolumna z popupu w nagłówku (Krok 2.F kreatora widoków list).
  // Przekazywane do ConfigurableTable jako controlled — backend aplikuje
  // je w drizzle WHERE, więc lokalne filtrowanie w CT nie odpala się.
  const [headerFilters, setHeaderFilters] = useState<ColumnHeaderFilters>({});
  // Filtry "Szukaj…" w wierszu pod nagłówkami (Krok 2.H — filter row).
  // Konwertowane w queryFn na ColumnHeaderFilter z mode='condition'
  // operator='contains' i mergowane z headerFilters do backendu.
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  // Zmiana filtrów w nagłówku wraca na pierwszą stronę — DWA callsite'y (ListFilterBar +
  // ConfigurableTable), więc zawinięte w jeden stabilny handler (jak w PurchaseInvoices.tsx).
  const handleHeaderFiltersChange = useCallback(
    (next: ColumnHeaderFilters) => {
      setHeaderFilters(next);
      controls.resetPage();
    },
    [controls.resetPage],
  );
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 300);

  // Tryb widoku (lista / karty / Kanban / kalendarz) — przełącznik w nagłówku strony,
  // tabela dostaje go jako prop sterowany i sama zapisuje `defaultViewMode`.
  // Stan startowy podnosi `ConfigurableTable` z zapisanego layoutu; pastylki = tryby
  // dostępne w bieżącym layoucie (raport tabeli) — wspólny hook.
  const viewModes = useListViewMode();

  // Wymuszony layout listy z pozycji Kreatora menu, z której user przyszedł
  // (kartoteka-layout-per-menu) — ta sama zmienna `tableId` musi iść do WSZYSTKICH
  // konsumentów niżej (ListTopWidgets, ListFilterBar, ConfigurableTable), inaczej
  // widżety i tabela renderują dwa różne layouty.
  const forcedListLayoutId = useForcedListLayoutId(CONTRACTORS_KEY);
  const tableId = forcedListLayoutId
    ? `${CONTRACTORS_KEY}::layout-${forcedListLayoutId}`
    : CONTRACTORS_KEY;

  // Dokleja `?mi=<id>` do nawigacji w głąb (nowy/detal), żeby formularz kontrahenta
  // znał kontekst pozycji menu (viewRef) — URL detalu jest osobną nawigacją, nie
  // dziedziczy query stringa listy.
  const withMenuItem = useWithMenuItem();

  // Szybkie filtry (presety z list_config) — chipy nad listą.
  const { layout: listLayout, isLoading: layoutLoading } = useTableLayout(tableId);
  const quickFilters = useMemo(
    () => listLayout?.layoutConfig?.quickFilters ?? [],
    [listLayout?.layoutConfig?.quickFilters],
  );

  // Ustawienia strony + przyciski z Kreatora Listy (zakładki „Strona"/„Przyciski").
  const pageConfig = listLayout?.layoutConfig?.pageConfig;
  const buttonsConfig = useMemo(
    () => listLayout?.layoutConfig?.buttonsConfig ?? [],
    [listLayout?.layoutConfig?.buttonsConfig],
  );
  // Etykieta standardowego przycisku — własna z Kreatora lub domyślna.
  // Własne przyciski (pasek / wiersz / masowe) rozkłada wspólny hook akcji listy.
  const labelFor = (actionId: string, fallback: string) =>
    buttonsConfig.find((b) => b.actionId === actionId)?.customLabel?.trim() || fallback;
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);
  useEffect(() => {
    if (activeQuickFilterId === null && quickFilters.length > 0) {
      const def = quickFilters.find((q) => q.isDefault);
      if (def) setActiveQuickFilterId(def.id);
    }
  }, [quickFilters, activeQuickFilterId]);

  // Kolumny pól dodatkowych (ext, isCore=false) generowane z katalogu pól modułu
  // (wspólny builder). `id` kolumny = slug pola — to samo id trafia do
  // `list_config.columns` (Kreator Listy) i do `?columns=` w zapytaniu listy.
  // UWAGA: musi być zdefiniowane PRZED `queryKey`/`listQ` niżej (visibleColumnParams
  // wchodzi do queryKey synchronicznie — odwrotna kolejność = ReferenceError z TDZ).
  // `getRefLabels` — getter o stałej tożsamości (patrz `useExtRefLabelsGetter`),
  // dostarcza `listQ.data?.extRefLabels` do renderCell kolumn `reference` bez
  // wchodzenia w zależności tego useMemo (aktualizowany niżej, PO `listQ`).
  const [getRefLabels, setRefLabels] = useExtRefLabelsGetter();
  const extColumns = useMemo<ColumnDefinition<ContractorRow>[]>(
    () => buildExtColumns<ContractorRow>(fieldsQ.data ?? [], t, getRefLabels),
    [fieldsQ.data, t, getRefLabels],
  );

  // Rejestr nadpisań kolumn rdzennych — memoizowany, bo `name` zamyka się nad `withMenuItem`
  // (forwarding `?mi=` z aktywnej pozycji menu do detalu kontrahenta, patrz `useWithMenuItem`).
  const contractorOverrides = useMemo(() => buildContractorOverrides(withMenuItem), [withMenuItem]);

  // Kolumny rdzenne z katalogu (klucz mechaniczny ze sluga; garstka przez rejestr nadpisań —
  // nazwa/typ podmiotu/status). Firma/opiekun/przypisany wykluczone (dedykowany builder / brak
  // kolumny na tej liście, patrz `CONTRACTORS_EXCLUDE_SLUGS`).
  const coreColumns = useMemo<ColumnDefinition<ContractorRow>[]>(
    () =>
      buildCoreColumns<ContractorRow>({
        fields: fieldsQ.data ?? [],
        t,
        getRefLabels,
        overrides: contractorOverrides,
        excludeSlugs: CONTRACTORS_EXCLUDE_SLUGS,
        resolveSortable: (f) => controls.isCoreColumnSortable(f.slug, sortableSlugs),
        resolveFilterable: (f) => controls.isCoreColumnFilterable(f.slug, filterableSlugs),
      }),
    [
      fieldsQ.data,
      t,
      getRefLabels,
      controls.isCoreColumnSortable,
      sortableSlugs,
      controls.isCoreColumnFilterable,
      filterableSlugs,
      contractorOverrides,
    ],
  );

  // Definicja kolumn dla ConfigurableTable: kolumny rdzenne z katalogu + „Firma" (dedykowany
  // builder, home_company_id) + kolumny ext dogenerowane dynamicznie z katalogu pól.
  const columns = useMemo<ColumnDefinition<ContractorRow>[]>(
    () => [
      ...coreColumns,
      buildCompanyColumn<ContractorRow>({
        companies,
        getRefLabels,
        t,
        ...CONTRACTOR_COMPANY_COLUMN_ARGS,
      }),
      // Kolumna „Kategoria" — wspólny builder (`@/lib/ext-list-columns`), JEDNO źródło prawdy
      // dla wszystkich kartotek/list z osią `record_categories` (Kontrahenci = pilot + 7 klas
      // dokumentów). Sort serwerowy po slugu `category` (autorytatywnie z `meta.sortable` silnika).
      buildCategoryColumn<ContractorRow>({
        t,
        resolveSortable: (slug) => controls.isCoreColumnSortable(slug, sortableSlugs),
      }),
      ...extColumns,
    ],
    [
      coreColumns,
      companies,
      getRefLabels,
      t,
      extColumns,
      controls.isCoreColumnSortable,
      sortableSlugs,
    ],
  );

  // Mapa id kolumny → slug backendu dla `?columns=`. id = slug wszędzie poza „Firmą" (id `company`).
  const coreIdToSlug = useMemo(() => {
    const map: Record<string, string> = { ...CONTRACTOR_COLUMN_ID_TO_SLUG };
    for (const c of coreColumns) map[c.id] = c.id; // id = slug (buildCoreColumns)
    return map;
  }, [coreColumns]);

  // Kolumny aktualnie widoczne, jako slugi backendu (wspólny helper) — do zbudowania
  // `?columns=`, żeby zapytanie listy ciągnęło tylko dane faktycznie renderowane.
  // Towarzysz nazwa→krótka nazwa: podpis „krótka nazwa" pod nazwą kontrahenta czyta
  // `row.shortName`, a to nie jest osobna kolumna — bez wymuszenia zniknąłby w projekcji.
  const visibleColumnParams = useMemo(
    () =>
      computeVisibleColumnParams({
        columns,
        savedLayoutColumns: listLayout?.layoutConfig?.columns,
        coreIdToSlug,
        extSlugSet: new Set(extColumns.map((c) => c.id)),
        // `source` (chip „Symfonia" przy nazwie) zawsze + towarzysz nazwa→krótka nazwa — patrz
        // `CONTRACTOR_ALWAYS_SLUGS` / `CONTRACTOR_COLUMN_COMPANIONS`.
        alwaysSlugs: CONTRACTOR_ALWAYS_SLUGS,
        companions: CONTRACTOR_COLUMN_COMPANIONS,
        // Pola kalendarza układu zawsze w `?columns=` — kalendarz nie zależy od widocznych kolumn.
        calendarConfig: listLayout?.layoutConfig?.calendarConfig,
      }),
    [
      columns,
      listLayout?.layoutConfig?.columns,
      listLayout?.layoutConfig?.calendarConfig,
      coreIdToSlug,
      extColumns,
    ],
  );

  const queryKey = [
    CONTRACTORS_KEY,
    {
      q: debouncedSearch,
      companyId: companyId ?? 'all',
      groups: selectedGroups,
      headerFilters,
      columnFilters: debouncedColumnFilters,
      quickFilterId: activeQuickFilterId,
      // Zbiór aktualnie widocznych kolumn (core-slug/ext-slug, patrz `visibleColumnParams`
      // niżej) — zmienia się gdy user przełączy widoczność kolumny (Kreator Listy lub
      // gear ConfigurableTable) albo gdy doładuje się katalog pól ext; wchodzi do klucza,
      // żeby zapytanie odświeżyło `?columns=` zamiast serwować stary cache.
      columns: visibleColumnParams,
      calendarRange: controls.calendarRange,
      ...controls.queryParams,
    },
  ];
  // Parametry filtrów listy BEZ stronicowania i projekcji kolumn — wspólne dla zapytania listy
  // i eksportu CSV (E16: plik zawiera dokładnie to, co lista). Zwykła funkcja, nie hook: czyta
  // stan strony w chwili wywołania (zapytanie listy / pobranie eksportu).
  // `includeCalendar`: miesiąc widoku kalendarza to nawigacja, nie filtr — zawężamy nim wyłącznie
  // zapytanie listy; eksport CSV (jak eksport modułów własnych) go pomija.
  function buildListFilterParams({
    includeCalendar,
  }: {
    includeCalendar: boolean;
  }): URLSearchParams {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (companyId) params.set('companyId', companyId);
    if (selectedGroups.length > 0) params.set('groups', selectedGroups.join(','));
    // Sort serwerowy (silnik list) — `sort` pominięty gdy brak (spada na domyślny sort
    // backendu), `order` zawsze wysyłany.
    if (controls.queryParams.sort) params.set('sort', controls.queryParams.sort);
    params.set('order', controls.queryParams.order);

    // Filtry idą do backendu jako pojedyncza tablica `headerFilters`:
    // (1) z popupu w nagłówku (mode=values/condition/range) +
    // (2) z input "Szukaj…" pod headerem (konwersja na mode=condition,
    //     operator=contains).
    const merged: ColumnHeaderFilter[] = [];

    for (const [columnId, filter] of Object.entries(headerFilters)) {
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
      if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
    }

    for (const [columnId, val] of Object.entries(debouncedColumnFilters)) {
      if (val == null || val === '') continue;
      const col = columns.find((c) => c.id === columnId);
      const fieldSlug = col?.headerFilter?.fieldSlug;
      if (!fieldSlug) continue; // brak headerFilter → nie da się zmapować
      merged.push({
        field: fieldSlug,
        mode: 'condition',
        operator: 'contains',
        conditionValue: String(val),
      });
    }

    // Aktywny szybki filtr — warunki presetu (pole=columnId → fieldSlug).
    const activeQf = quickFilters.find((q) => q.id === activeQuickFilterId);
    if (activeQf) {
      for (const cond of activeQf.filters) {
        const col = columns.find((c) => c.id === cond.field);
        // Pola spoza kolumn (Opiekun/Właściciel) niosą slug wprost jako `cond.field`.
        const fieldSlug =
          col?.headerFilter?.fieldSlug ??
          (isExtraFilterField(CONTRACTORS_KEY, cond.field) ? cond.field : undefined);
        if (!fieldSlug) continue;
        // `@userId` (i dawne `@ja`) → id zalogowanego; nierozwiązane (sesja się ładuje) pomijamy.
        const value = resolveFilterVariable(cond.value, me?.user.id);
        if (value === null) continue;
        merged.push({
          field: fieldSlug,
          mode: 'condition',
          operator: cond.operator as ColumnHeaderFilter['operator'],
          conditionValue: value == null ? '' : String(value),
        });
      }
    }

    // Widok kalendarza dociąga tylko rekordy z wyświetlanego miesiąca (wspólny filtr z silnika) —
    // tylko dla listy, nie dla eksportu.
    if (includeCalendar && controls.calendarHeaderFilter) {
      merged.push(controls.calendarHeaderFilter);
    }
    if (merged.length > 0) {
      params.set('headerFilters', JSON.stringify(merged));
    }
    return params;
  }

  const listQ = useQuery({
    queryKey,
    // Trzyma poprzednie dane na ekranie podczas refetchu (zmiana strony/sortu/filtra) — bez
    // tego lista miga loaderem przy każdej zmianie.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = buildListFilterParams({ includeCalendar: true });
      if (visibleColumnParams.length > 0) {
        params.set('columns', visibleColumnParams.join(','));
      }
      // Paginacja serwerowa (silnik list) — `limit`/`offset` zawsze wysyłane.
      params.set('limit', String(controls.queryParams.limit));
      params.set('offset', String(controls.queryParams.offset));

      const url = params.toString() ? `/contractors?${params.toString()}` : '/contractors';
      const res = await api.get<ListResponse>(url);
      return res.data;
    },
  });
  setRefLabels(listQ.data?.extRefLabels);
  // `meta.sortable` w stan przez EFEKT (nie w trakcie renderu — to prawdziwy useState, wywołanie
  // w renderze daje nieskończoną pętlę; `setRefLabels` wolno w renderze, bo to setter refa).
  // Efekt odpala się, gdy metadane dotrą → kolumny core przeliczają sortowalność (patrz `columns`).
  useEffect(() => {
    setSortableSlugs(listQ.data?.meta?.sortable);
  }, [listQ.data?.meta?.sortable]);
  useEffect(() => {
    setFilterableSlugs(listQ.data?.meta?.filterable);
  }, [listQ.data?.meta?.filterable]);

  // Drag & drop na tablicy Kanban → zmiana statusu kontrahenta.
  // `onRowUpdate` z ConfigurableTable woła to z (rowId, 'status', newValue).
  const statusUpdateMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/contractors/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'contractors'] });
    },
    onError: (err) => {
      // Zmiana statusu przez przeciągnięcie to zwykły zapis rekordu — na czas zadania przycisku SQL serwer
      // odpowiada 409 `RECORD_LOCKED_BY_RUN`; zamiast ogólnego błędu pokazujemy, które zadanie blokuje
      // rekord (kafelek wraca na miejsce — rollback robi sama tablica).
      const conflict = extractRunConflict(err);
      if (conflict?.code === 'RECORD_LOCKED_BY_RUN') {
        toast.show({
          variant: 'error',
          title: runConflictText(conflict, NO_BUTTON_LABELS, t, i18n.language),
        });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.contractors.toast.updateError') });
    },
  });

  // Eksport CSV (E16) — dokładnie to, co lista. „Eksport listy": te same filtry co zapytanie
  // listy (wyszukiwarka, grupy, filtry kolumn, szybki filtr, sort, wybrana firma), wszystkie
  // strony. „Eksport zaznaczonych": tylko wskazane ID (+ firma i sort listy). Plik zawiera
  // WYŁĄCZNIE kolumny widoczne w tabeli, w jej kolejności i z jej nagłówkami; wartości wylicza
  // ekran (etykiety, formaty), serwer oddaje surowe wiersze tylko tych kolumn.
  const extSlugSet = useMemo(() => new Set(extColumns.map((c) => c.id)), [extColumns]);
  const listExport = useEngineListExport<ContractorRow>({
    target: 'contractors',
    filename: 'kontrahenci',
    // Bez miesiąca kalendarza — to nawigacja widoku, nie filtr (plik = cały widok).
    buildFilterParams: () => buildListFilterParams({ includeCalendar: false }),
    columns,
    visibleColumnIds,
    // Dopóki katalog pól i układ listy się ładują, eksport nie startuje (niepełna lista kolumn).
    ready: fieldsQ.isSuccess && !layoutLoading,
    columnSlug: (id) => coreIdToSlug[id] ?? (extSlugSet.has(id) ? id : undefined),
    // Etykiety odwołań (firma, pola `reference`) z odpowiedzi eksportu — obejmują WSZYSTKIE
    // wyeksportowane wiersze, nie tylko bieżącą stronę listy. Te same buildery co tabela.
    buildExportColumns: (refLabels) => {
      const getLabels = () => refLabels as ExtRefLabels | undefined;
      return [
        ...buildCoreColumns<ContractorRow>({
          fields: fieldsQ.data ?? [],
          t,
          getRefLabels: getLabels,
          overrides: contractorOverrides,
          excludeSlugs: CONTRACTORS_EXCLUDE_SLUGS,
        }),
        buildCompanyColumn<ContractorRow>({
          companies,
          getRefLabels: getLabels,
          t,
          ...CONTRACTOR_COMPANY_COLUMN_ARGS,
        }),
        buildCategoryColumn<ContractorRow>({ t }),
        ...buildExtColumns<ContractorRow>(fieldsQ.data ?? [], t, getLabels),
      ];
    },
    // Komórka „Nazwa" na ekranie to nazwa + plakietka źródła + podpis nazwy skróconej — do pliku
    // idzie sama nazwa kontrahenta (reszta to ozdobniki, nie wartość kolumny).
    textOverrides: { name: (row) => row.name ?? '' },
    layoutColumns: listLayout?.layoutConfig?.columns,
    formattingRules: listLayout?.layoutConfig?.formattingRules,
    heading: pageConfig?.title?.trim() || t('tenant.contractors.list.title'),
  });

  // Akcje listy (PHASE 25) — generyczny hook: resolver widoczności + wykonawca
  // (rozgłoszenie po ID wg katalogu) + menu kontekstowe + potwierdzenia. Escape-hatch
  // `handlers`: eksporty (zaznaczonych i całej listy) składa strona z widocznych kolumn,
  // a import otwiera modal strony. Bramka „Wszystkie firmy" blokuje archiwizację/import.
  const listActions = useListActions({
    kartotekaKey: 'contractors',
    entity: 'contractors',
    queryKey: [CONTRACTORS_KEY],
    selectedIds,
    setSelectedIds,
    listActionsConfig: listLayout?.layoutConfig?.listActions,
    customButtons: listLayout?.layoutConfig?.customActionButtons,
    handlers: {
      ...listExport.handlers,
      import: () => setImportOpen(true),
    },
    guard: (actionId) =>
      (actionId === 'delete' || actionId === 'import') && activeCompanyScope.isAll
        ? t('common.allModeActionBlocked')
        : null,
    testIdPrefix: 'contractors',
  });

  const contractors = listQ.data?.contractors ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-6" data-testid="contractors-page">
      <header className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-text-primary">
            {pageConfig?.title?.trim() || t('tenant.contractors.list.title')}
          </h1>
          <p className="mt-1 text-sm text-text-tertiary">
            {pageConfig?.description?.trim() || t('tenant.contractors.list.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListViewModeToggle {...viewModes.toggleProps} testIdPrefix="contractors" />
          <ListActionsMenu {...listActions.menuProps} />
          <ListBuilderMenu slug="contractors" testIdPrefix="contractors" />
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(withMenuItem('/contractors/new'))}
            leftIcon={<Plus size={16} aria-hidden="true" />}
            data-testid="contractors-add-btn"
          >
            {labelFor('create', t('tenant.contractors.list.addBtn'))}
          </Button>
        </div>
      </header>

      <div className="shrink-0">
        <ListTopWidgets tableId={tableId} categoryId={null} companyId={companyId} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {fieldsQ.isError ? (
          <FieldCatalogErrorState onRetry={() => void fieldsQ.refetch()} />
        ) : (
          <ConfigurableTable<ContractorRow>
            tableId={tableId}
            testIdPrefix="contractors"
            showOrdinal
            fillHeight
            /* Górny pasek listy WEWNĄTRZ karty tabeli (parytet z Figmą): chipy grup po lewej,
               baton odśwież/filtry po prawej; presety w osobnym rzędzie pod spodem. */
            listToolbar={
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Chipy filtra = GRUPY ze słownika (wielokrotny wybór, OR). */}
                  <GroupChipBar
                    dictionaryCode="contractor_groups"
                    selected={selectedGroups}
                    onChange={(next) => {
                      setSelectedGroups(next);
                      controls.resetPage();
                    }}
                    className="flex-1"
                    data-testid="contractors-group-chip-bar"
                  />
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    {pageConfig?.showFilterBar !== false && (
                      <ListFilterBar
                        tableId={tableId}
                        columns={columns}
                        search={search}
                        onSearchChange={(next) => {
                          setSearch(next);
                          controls.resetPage();
                        }}
                        searchPlaceholder={t('tenant.contractors.list.searchPlaceholder')}
                        showSearch={false}
                        headerFilters={headerFilters}
                        onHeaderFiltersChange={handleHeaderFiltersChange}
                        onRefresh={() =>
                          queryClient.invalidateQueries({ queryKey: [CONTRACTORS_KEY] })
                        }
                        testIdPrefix="contractors"
                        filterBarStyle={pageConfig?.filterBarStyle}
                      />
                    )}
                  </div>
                </div>
                <QuickFilterBar
                  quickFilters={quickFilters}
                  activeId={activeQuickFilterId}
                  onChange={(id) => {
                    setActiveQuickFilterId(id);
                    controls.resetPage();
                  }}
                />
              </div>
            }
            columns={columns}
            data={contractors}
            isLoading={listQ.isLoading}
            getRowId={(row) => row.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onVisibleColumnsChange={setVisibleColumnIds}
            headerFilters={headerFilters}
            onHeaderFiltersChange={handleHeaderFiltersChange}
            filterable
            columnFilters={columnFilters}
            onColumnFiltersChange={(next) => {
              setColumnFilters(next);
              controls.resetPage();
            }}
            {...controls.tableProps}
            totalItems={listQ.data?.total ?? 0}
            isFetching={listQ.isFetching}
            onCalendarRangeChange={controls.setCalendarRange}
            {...viewModes.tableProps}
            onRowClick={(row) => navigate(withMenuItem(`/contractors/${row.id}`))}
            onRowContextMenu={listActions.onRowContextMenu}
            onRowUpdate={async (rowId, _fieldId, newValue) => {
              // Kontrahent zawsze ma status — przeciągnięcie do „Nieprzypisane"
              // (newValue=null) nie ma sensu, odrzucamy (board zrobi rollback).
              if (newValue == null) throw new Error('STATUS_REQUIRED');
              await statusUpdateMutation.mutateAsync({ id: rowId, status: newValue });
            }}
            renderActions={(row) => (
              <div className="flex justify-end">
                <RowActionsMenu
                  testId={`contractors-row-${row.id}-actions`}
                  items={[
                    {
                      key: 'edit',
                      label: t('common.edit'),
                      icon: Pencil,
                      onSelect: () => navigate(withMenuItem(`/contractors/${row.id}`)),
                      testId: `contractors-row-${row.id}-edit`,
                    },
                    {
                      key: 'transfer',
                      label: t('tenant.contractors.list.transferAria'),
                      icon: ArrowRightLeft,
                      onSelect: () =>
                        navigate(withMenuItem(`/contractors/${row.id}?tab=company_access`)),
                      testId: `contractors-row-${row.id}-transfer`,
                    },
                    // Akcje z katalogu list na TYM rekordzie — pod dividerem (PHASE 25):
                    // archiwizuj, eksport (+ KSeF/FK dla dokumentów). Edytuj/przenieś
                    // zostają natywne u góry.
                    ...listActions.rowCatalogActionItems(row.id),
                  ]}
                />
              </div>
            )}
            actionsColumnLabel={t('tenant.contractors.list.column.actions')}
            emptyIcon={Building2}
            emptyTitle={t('tenant.contractors.list.empty')}
          />
        )}
      </div>

      <ContractorImportModal open={importOpen} onClose={() => setImportOpen(false)} />

      {listActions.overlays}
    </div>
  );
}
